// packages/app-features/lib/features/steps-manager/hooks/use-program-status-modal.ts
import {
  ClientProgramStatus,
  Program,
  ProgramNeededProperties,
  ProgramUpdateProperties,
} from '@hellodarwin/core/lib/features/entities/programs-entities';
import { useMemo, useState } from 'react';
import { getModalRequirementsForStatusChange } from '../features/utils/application-status-modal';

type ModalState = {
  open: boolean;
  neededProperties: ProgramNeededProperties;
  onConfirm: (properties: ProgramUpdateProperties) => void;
};

const CLOSED_APPLICATION_STATUS_MODAL: ModalState = {
  open: false,
  neededProperties: {
    draft_first_version: {
      amount: null,
    },
    submit_application: {
      time: null,
      amount: null,
    },
    decisions_made: {
      time: null,
      amount: null,
      refusal_reason: null,
    },
  },
  onConfirm: () => {},
};

/**
 * Small convenience hook: given two statuses and a program, decide if the modal should open.
 * If it should, it sets the local modal state with prefilled neededProperties and your onConfirm handler.
 */
export function useApplicationStatusModal() {
  const [modal, setModal] = useState<ModalState>(
    CLOSED_APPLICATION_STATUS_MODAL,
  );

  const close = () => {
    setModal((m) => ({ ...m, open: false }));
  };

  const openIfNeeded = (
    initialStatus: ClientProgramStatus | undefined,
    nextStatus: ClientProgramStatus | undefined,
    program: Program,
    onConfirm: (properties: ProgramUpdateProperties) => void,
  ): boolean => {
    const { shouldOpen, neededProperties } =
      getModalRequirementsForStatusChange(initialStatus, nextStatus, program);

    if (!shouldOpen) return false;

    setModal({
      open: true,
      neededProperties,
      onConfirm,
    });
    return true;
  };

  // Consumers can pass modal.open, modal.neededProperties, modal.onConfirm to <StepsManagerModal />
  return useMemo(() => ({ modal, setModal, close, openIfNeeded }), [modal]);
}
